import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	IMoodBoardComment,
	IMoodBoardPatchPayload,
} from 'api/resources/moodBoards';
import { getMoodBoard, patchMoodBoard } from 'api/resources/moodBoards';
import { ECacheKeys } from 'cache/CacheKeys';

// Surfaced by the global mutation toast in place of axios' status-code text.
export const SAVE_COMMENTS_ERROR =
	'Your comment could not be saved. Please try again.';
export const SAVE_STATUS_ERROR =
	'The mood board status could not be updated. Please try again.';

// Updates a board through the single update endpoint, whether that is its
// comment set (from the open board) or its status (from the list's cards).
//
// It resolves with the board's comment set as the BE holds it afterwards, so a
// comment save can adopt the ids assigned to newly posted comments. The update
// endpoint's own response is not relied on for those: the board is refetched
// instead, which also refreshes the cached record without remounting the
// canvas (the query already has data, so the page never drops back to its
// loading state, and Excalidraw only reads its scene on mount). That refetch
// only happens when the board is already cached, i.e. it is open; from the
// list the record is merely invalidated. The list itself is always invalidated
// so every card shows the new status. A failed refetch is not a failed save,
// so it resolves with no comments rather than rejecting. Success and failure
// toasts come from the global mutation cache.
export const usePatchMoodBoard = (token: string, id?: string) => {
	const queryClient = useQueryClient();
	const queryKey = [ECacheKeys.MoodBoard, id];

	return useMutation<IMoodBoardComment[] | null, Error, IMoodBoardPatchPayload>(
		{
			mutationFn: async (payload) => {
				try {
					await patchMoodBoard(token, id as string, payload);
				} catch {
					throw new Error(
						payload.approvalStatus !== undefined
							? SAVE_STATUS_ERROR
							: SAVE_COMMENTS_ERROR,
					);
				}

				await queryClient.invalidateQueries({
					queryKey: [ECacheKeys.MoodBoards],
				});

				if (queryClient.getQueryData(queryKey) === undefined) {
					await queryClient.invalidateQueries({ queryKey });

					return null;
				}

				try {
					const board = await queryClient.fetchQuery({
						queryKey,
						queryFn: () => getMoodBoard(token, id as string),
						staleTime: 0,
					});

					return board.comments;
				} catch {
					return null;
				}
			},
			onError: (error) => {
				// eslint-disable-next-line no-console
				console.error('Failed to update mood board:', error);
			},
		},
	);
};
